// Cognito SRP auth helpers + RequireAuth wrapper.
//
// Reference: PRPs/phone_lookup_rtb_api_prp_v2.md §13.2 (Login page), Locked
// L19 (MFA TOTP). We use amazon-cognito-identity-js for in-page SRP rather
// than the hosted UI to avoid custom-domain CORS pitfalls (see issue
// description anti-patterns).
//
// Anti-patterns explicitly avoided here:
//   - No persisting plaintext credentials. The library stores only the JWT
//     pair (id_token + refresh_token) in localStorage, scoped per user pool.
//   - No bypassing RequireAuth. We export a single guard the App uses on
//     every protected route.
//   - No silent re-throw of NEW_PASSWORD_REQUIRED / SOFTWARE_TOKEN_MFA — the
//     login form handles those specific challenges explicitly.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type ISignUpResult,
} from "amazon-cognito-identity-js";
import { api, type Section } from "./api";

// ---------------------------------------------------------------------------
// Pool config — read once at module load.
// ---------------------------------------------------------------------------

interface PoolConfig {
  UserPoolId: string;
  ClientId: string;
  Region: string;
  HostedUiUrl: string | null;
}

let cachedPool: CognitoUserPool | null = null;

function readEnv(): PoolConfig {
  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  return {
    UserPoolId: env["VITE_COGNITO_USER_POOL_ID"] ?? "",
    ClientId: env["VITE_COGNITO_CLIENT_ID"] ?? "",
    Region: env["VITE_COGNITO_REGION"] ?? "",
    HostedUiUrl: env["VITE_COGNITO_HOSTED_UI_URL"] ?? null,
  };
}

export function getPool(): CognitoUserPool {
  if (cachedPool) return cachedPool;
  const cfg = readEnv();
  if (!cfg.UserPoolId || !cfg.ClientId) {
    throw new Error(
      "Cognito not configured: set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID",
    );
  }
  cachedPool = new CognitoUserPool({
    UserPoolId: cfg.UserPoolId,
    ClientId: cfg.ClientId,
  });
  return cachedPool;
}

export function getHostedUiUrl(): string | null {
  return readEnv().HostedUiUrl;
}

// ---------------------------------------------------------------------------
// Auth context — exposes the current session + sign-in/out helpers.
// ---------------------------------------------------------------------------

export interface AuthUser {
  username: string;
  email: string | null;
  sub: string | null;
  /** Live from GET /api/me (never the 8h-stale cognito:groups token claim). */
  isAdmin: boolean;
  /** Granted dashboard sections, live from GET /api/me. */
  sections: Section[];
}

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<AuthSignInResult>;
  signOut: () => Promise<void>;
  /** Returns the current id_token JWT or null. Refreshes as needed. */
  getIdToken: () => Promise<string | null>;
  /**
   * Re-fetch GET /api/me and update the signed-in user's live grants
   * (isAdmin/sections) in place. No-op when signed out. Used by the Users
   * page after a change that may affect the caller's own access.
   */
  refreshGrants: () => Promise<void>;
  /** Submit a TOTP 6-digit code to satisfy a runtime MFA challenge. */
  verifyTotp: (
    cognitoUser: CognitoUser,
    code: string,
  ) => Promise<AuthSignInResult>;
  /** Begin first-time TOTP enrollment. Returns a secretCode for the QR. */
  associateTotp: (cognitoUser: CognitoUser) => Promise<string>;
  /** Confirm a first-time TOTP code, set software-token as the preferred MFA. */
  verifyAndSetTotp: (
    cognitoUser: CognitoUser,
    code: string,
    deviceName?: string,
  ) => Promise<AuthSignInResult>;
}

export type AuthSignInResult =
  | { kind: "ok"; user: AuthUser }
  | { kind: "newPasswordRequired"; cognitoUser: CognitoUser }
  | { kind: "mfaRequired"; cognitoUser: CognitoUser; mfaType: string }
  | { kind: "mfaSetupRequired"; cognitoUser: CognitoUser }
  | { kind: "error"; message: string };

const AuthContext = createContext<AuthState | null>(null);

/** The id_token base identity, before live section grants are attached. */
type BaseIdentity = Pick<AuthUser, "username" | "email" | "sub">;

function userFromSession(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
): BaseIdentity {
  const idPayload = session.getIdToken().decodePayload() as Record<
    string,
    unknown
  >;
  const email =
    typeof idPayload["email"] === "string"
      ? (idPayload["email"] as string)
      : null;
  const sub =
    typeof idPayload["sub"] === "string" ? (idPayload["sub"] as string) : null;
  return {
    username: cognitoUser.getUsername(),
    email,
    sub,
  };
}

/**
 * Build the fully-populated authenticated user: the id_token base identity
 * PLUS the live section grants from GET /api/me. Grants ALWAYS come from
 * /api/me — never the cognito:groups token claim, which is up to 8h stale
 * (PRP gotchas #4/#5). Rejects if /api/me fails so callers fail closed.
 */
async function loadAuthUser(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
): Promise<AuthUser> {
  const base = userFromSession(cognitoUser, session);
  const me = await api.me();
  return { ...base, isAdmin: me.is_admin, sections: me.sections };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount: try to restore the cached session synchronously.
  useEffect(() => {
    let cancelled = false;
    try {
      const pool = getPool();
      const cognitoUser = pool.getCurrentUser();
      if (!cognitoUser) {
        setLoading(false);
        return;
      }
      cognitoUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (cancelled) return;
          if (err || !session || !session.isValid()) {
            setUser(null);
            setLoading(false);
            return;
          }
          // Valid Cognito session — augment the identity with the live section
          // grants from GET /api/me BEFORE clearing the loading gate, so
          // RequireAuth never renders ungated nav (PRP gotcha #5). Grants come
          // from /api/me, never the 8h-stale cognito:groups token claim.
          loadAuthUser(cognitoUser, session).then(
            (u) => {
              if (cancelled) return;
              setUser(u);
              setLoading(false);
            },
            () => {
              // Fail closed: /api/me rejected (401 / outage) → sign out.
              if (cancelled) return;
              void signOut();
              setLoading(false);
            },
          );
        },
      );
    } catch {
      if (!cancelled) setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (
      username: string,
      password: string,
    ): Promise<AuthSignInResult> => {
      const pool = getPool();
      const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      return new Promise<AuthSignInResult>((resolve) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            // Load live grants before exposing the user; fail closed if /api/me
            // rejects (do NOT set a half-populated, ungated user).
            loadAuthUser(cognitoUser, session).then(
              (u) => {
                setUser(u);
                resolve({ kind: "ok", user: u });
              },
              (err: unknown) => {
                void signOut();
                resolve({
                  kind: "error",
                  message: err instanceof Error ? err.message : String(err),
                });
              },
            );
          },
          onFailure: (err: Error) => {
            resolve({ kind: "error", message: err.message ?? String(err) });
          },
          newPasswordRequired: () => {
            resolve({ kind: "newPasswordRequired", cognitoUser });
          },
          totpRequired: (mfaType: string) => {
            resolve({ kind: "mfaRequired", cognitoUser, mfaType });
          },
          mfaRequired: (mfaType: string) => {
            resolve({ kind: "mfaRequired", cognitoUser, mfaType });
          },
          mfaSetup: () => {
            resolve({ kind: "mfaSetupRequired", cognitoUser });
          },
        });
      });
    },
    [],
  );

  const verifyTotp = useCallback(
    (cognitoUser: CognitoUser, code: string): Promise<AuthSignInResult> => {
      return new Promise((resolve) => {
        cognitoUser.sendMFACode(
          code,
          {
            onSuccess: (session) => {
              loadAuthUser(cognitoUser, session).then(
                (u) => {
                  setUser(u);
                  resolve({ kind: "ok", user: u });
                },
                (err: unknown) => {
                  void signOut();
                  resolve({
                    kind: "error",
                    message: err instanceof Error ? err.message : String(err),
                  });
                },
              );
            },
            onFailure: (err: Error) => {
              resolve({ kind: "error", message: err.message ?? String(err) });
            },
          },
          "SOFTWARE_TOKEN_MFA",
        );
      });
    },
    [],
  );

  const associateTotp = useCallback(
    (cognitoUser: CognitoUser): Promise<string> => {
      return new Promise((resolve, reject) => {
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode: string) => resolve(secretCode),
          onFailure: (err: Error) =>
            reject(err instanceof Error ? err : new Error(String(err))),
        });
      });
    },
    [],
  );

  const verifyAndSetTotp = useCallback(
    (
      cognitoUser: CognitoUser,
      code: string,
      deviceName: string = "Authenticator",
    ): Promise<AuthSignInResult> => {
      return new Promise((resolve) => {
        cognitoUser.verifySoftwareToken(code, deviceName, {
          onSuccess: (session) => {
            // Set TOTP as the preferred MFA so future sign-ins challenge for it.
            cognitoUser.setUserMfaPreference(
              null,
              { PreferredMfa: true, Enabled: true },
              () => {
                /* swallow — preference set is best-effort here */
              },
            );
            loadAuthUser(cognitoUser, session).then(
              (u) => {
                setUser(u);
                resolve({ kind: "ok", user: u });
              },
              (err: unknown) => {
                void signOut();
                resolve({
                  kind: "error",
                  message: err instanceof Error ? err.message : String(err),
                });
              },
            );
          },
          onFailure: (err: Error) => {
            resolve({ kind: "error", message: err.message ?? String(err) });
          },
        });
      });
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      const pool = getPool();
      const cu = pool.getCurrentUser();
      cu?.signOut();
    } catch {
      // pool may be unconfigured in tests; ignore
    }
    // Wipe any session-scoped plaintext API keys cached by the client
    // detail / new-client pages. Keys: `loop-rtb:apikey:<client_id>`.
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith("loop-rtb:apikey:")) {
          sessionStorage.removeItem(k);
        }
      }
    } catch {
      /* sessionStorage may be unavailable in some environments */
    }
    setUser(null);
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const pool = getPool();
      const cu = pool.getCurrentUser();
      if (!cu) return null;
      return await new Promise<string | null>((resolve) => {
        cu.getSession(
          (err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session || !session.isValid()) {
              resolve(null);
            } else {
              resolve(session.getIdToken().getJwtToken());
            }
          },
        );
      });
    } catch {
      return null;
    }
  }, []);

  const refreshGrants = useCallback(async (): Promise<void> => {
    if (!user) return; // no-op when signed out
    const me = await api.me();
    setUser((prev) =>
      prev ? { ...prev, isAdmin: me.is_admin, sections: me.sections } : prev,
    );
  }, [user]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      getIdToken,
      refreshGrants,
      verifyTotp,
      associateTotp,
      verifyAndSetTotp,
    }),
    [
      user,
      loading,
      signIn,
      signOut,
      getIdToken,
      refreshGrants,
      verifyTotp,
      associateTotp,
      verifyAndSetTotp,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

/**
 * Route guard. Renders children only if there is an authenticated user.
 * Otherwise redirects to /login, preserving the original location for a
 * post-login bounce.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div>Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

/**
 * Convenience helper for tests / non-component callers (e.g. signing up
 * the first admin user via console). Mirrors the cognito-identity-js
 * signature so tests can stub it cleanly.
 */
export function signUp(
  username: string,
  password: string,
): Promise<ISignUpResult> {
  return new Promise((resolve, reject) => {
    getPool().signUp(username, password, [], [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error("signUp returned no result"));
      } else {
        resolve(result);
      }
    });
  });
}
